import { Message } from "@me8eon/messages";

import { AgentCard, AgentCards, HasLastSelected, HasType } from "../agent.card";
import { NameAnd } from "@me8eon/record_utils";
import { ErrorsOr, isErrors, LogAnd } from "@me8eon/errors";

export type ContextAndMessagesAndSelected<Context> = {
    context: Context
    selected?: string
    messages: Message[]
}

export type SelectorContext<Context> = {
    cards: AgentCards<Context, any, any>
    selFns: SelectorFns<Context>
    selectedAgent: AgentCard<Context, any> | undefined
}
export type SelectorExecuteFn<Context, Selector> = (selector: Selector, context: Context, messages: Message[], sc: SelectorContext<Context>) => Promise<LogAnd<ErrorsOr<ContextAndMessagesAndSelected<Context>>>>
export type SelectorFn<Context, Selector> = {
    isDefinedAt?: (selector: Selector, context: Context, messages: Message[], sc: SelectorContext<Context>) => boolean
    execute: SelectorExecuteFn<Context, Selector>
}
export type SelectorFns<Context> = NameAnd<SelectorFn<Context, any>>

export type ContextAndMessagesAndSelectedAndCard<Context> = ContextAndMessagesAndSelected<Context> & {
    agentCard: AgentCard<Context, any>
}
export const selectAgent = async <Context extends HasLastSelected, Selector extends HasType>(sc: SelectorContext<Context>, context: Context, messages: Message[]): Promise<LogAnd<ErrorsOr<ContextAndMessagesAndSelectedAndCard<Context>>>> => {
    const { selFns, cards } = sc;
    const selector = cards.selector;
    const selFn = selFns[selector.type];
    if (!selFn) return {
        errors: [`selector ${selector.type} not found. Legal values are ${Object.keys(selFns).sort()}`],
        log: { whatHappened: `selector.type.not.found`, params: JSON.stringify(selector), severity: "error" },
    };
    if (selFn.isDefinedAt && !selFn.isDefinedAt(selector, context, messages, sc)) return {
        errors: [`selector.not.defined`],
        log: { whatHappened: `selector.not.defined`, params: [JSON.stringify(context), JSON.stringify(messages)], severity: "error" },
    };
    const result = await selFn.execute(selector, context, messages, sc);
    if (isErrors(result)) return result;
    const { selected: key, context: newContext, messages: newMessages } = result.value;
    const selected = Object.keys(cards.cards).find(name => name.toLowerCase() === key?.toLowerCase());
    if (!selected) return {
        errors: [`selector.card.not.found`, `Card ${key} not found. Legal values are ${Object.keys(cards.cards).sort()}`],
        log: { whatHappened: `selector.card.not.found`, params: { key, logs: result.log }, severity: "error" },
    };
    const value: ContextAndMessagesAndSelectedAndCard<Context> = {
        context: { ...newContext, lastSelected: selected },
        messages: newMessages,
        selected,
        agentCard: cards.cards[selected],
    };
    return {
        value,
        log: {
            whatHappened: `selector.card.found`, params: { log: result.log },
        },
    };
};

